"use client"

import clsx from "clsx"
import Image, { ImageProps } from "next/image"
import React, { useMemo } from "react"

import { StrictReactNode } from "@/types/react"
import DistributedColumnFLexLayout from "@/ui/components/DistributedColumnFlexLayout/DistributedColumnFlexLayout"
import PageGrid from "@/ui/components/PageGrid"
import SectionWrapper from "@/ui/components/SectionWrapper"

/**
 * Certification Item Props
 * @param image {ImageProps} - Image props
 * @param title {StrictReactNode} - Title
 * @param type {StrictReactNode} - Type
 * @param asterisk {StrictReactNode} - Asterisk
 */
export type CertificationItemProps = {
  image: ImageProps
  title: StrictReactNode
  type: StrictReactNode
  asterisk?: StrictReactNode
}

/**
 * Certification List Props
 * @param items {CertificationItemProps[]} - Items
 */
export type CertificationListProps =
  React.ComponentPropsWithoutRef<"section"> & {
    items: Array<CertificationItemProps>
  }

/**
 * Certification List Component
 * @returns Certification List Component section
 */
export const CertificationListSection: React.FC<CertificationListProps> = (
  props,
) => {
  const { items, className, ...rest } = props

  /**
   * Certification Items
   *
   * This is a memoized function that maps the items to the certification items
   * If the item has an asterisk, The type will be appended with an asterisk
   * and the asterisk will be added to the asterisks array
   *
   * @param items {CertificationItemProps[]} - Items
   * @returns Certification Items
   */
  const certificationItems = useMemo(() => {
    // This is a counter for the number of asterisks
    let asteriskCount = 1

    // This is a memoized function that maps the items to the certification items
    return items.reduce<{
      mappedItems: CertificationItemProps[]
      asterisks: StrictReactNode[]
    }>(
      (acc, item) => {
        if (item?.asterisk) {
          // create a string of asterisks based on the number of asterisks
          const asterisk = Array.from({ length: asteriskCount })
            .fill("*")
            .join("")
          const Asterisk = <span className="text-gray-90">{asterisk}</span>

          acc.mappedItems.push({
            ...item,
            type: [item.type, Asterisk],
          })
          acc.asterisks.push([Asterisk, item.asterisk])
          asteriskCount += asteriskCount
        } else {
          acc.mappedItems.push(item)
        }
        return acc
      },
      { mappedItems: [], asterisks: [] },
    )
  }, [items])

  if (certificationItems.mappedItems.length === 0) {
    return null
  }

  return (
    <SectionWrapper
      className={clsx("bg-gray-2.5 text-gray-90", className)}
      {...rest}
    >
      <PageGrid isGrid={false} className="md:mx-8">
        <DistributedColumnFLexLayout numOfColsMd={3}>
          {certificationItems.mappedItems.map((item) => (
            <div
              key={item.image.src.toString()}
              className="flex  flex-col gap-4 text-center justify-items-center relative"
            >
              <Image
                {...item.image}
                className={clsx("mx-auto w-auto h-auto", item.image?.className)}
              />
              <div>
                <p className="heading2">{item.title}</p>
                <p className="body2">{item.type}</p>
              </div>
            </div>
          ))}
        </DistributedColumnFLexLayout>
        {certificationItems.asterisks.length > 0 && (
          <div className="block mt-8 lg:mt-14 col-span-full body4">
            {certificationItems.asterisks.map((asterisk, index) => (
              // this data is not dynamic therefore using the index is ok
              // eslint-disable-next-line react/no-array-index-key
              <p key={index}>{asterisk}</p>
            ))}
          </div>
        )}
      </PageGrid>
    </SectionWrapper>
  )
}

CertificationListSection.displayName = "CertificationListSection"
